import { CognitoUserPool, CognitoUserAttribute, CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
import { disconnectDeviceVpn } from "api/DeviceHelpers";
import { recordDiagnostic, reportError } from "utils/diagnostics";

const userPool = new CognitoUserPool({
  Region: process.env.REACT_APP_AWS_REGION,
  UserPoolId: process.env.REACT_APP_AWS_USER_POOL_ID,
  ClientId: process.env.REACT_APP_AWS_CLIENT_ID,
});

const loggedCallback = (operation, callback) => (error, result) => {
  if (error) reportError(operation, error);
  else recordDiagnostic("auth.completed", { operation });
  callback(error, result);
};

export const createUser = (email, password, name, phoneNumber, organization, callback) => {
  const attributeList = [
    new CognitoUserAttribute({
      Name: "name",
      Value: name,
    }),
    new CognitoUserAttribute({
      Name: "phone_number",
      Value: phoneNumber,
    }),
    new CognitoUserAttribute({
      Name: "preferred_username",
      Value: organization,
    }),
  ];

  // Username must be unique in a pool, and cant be a valid email format
  // To log in with email, make sure it is set as an alias attribute in Cognito
  // More info: http://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-attributes.html#user-pool-settings-usernames

  userPool.signUp(email, password, attributeList, null, loggedCallback("auth.signup", callback));
};

export const verifyUser = (email, verifyCode, callback) => {
  const userData = {
    Username: email,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);
  cognitoUser.confirmRegistration(verifyCode, true, loggedCallback("auth.verify", callback));
};

export const resendVerifyCode = (email, callback) => {
  const userData = {
    Username: email,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);
  cognitoUser.resendConfirmationCode(loggedCallback("auth.resend_code", callback));
};

export const authenticateUser = (email, password, authCallback, mfaCallback, newPasswordCallback) => {
  const authData = {
    Username: email,
    Password: password,
  };
  const authDetails = new AuthenticationDetails(authData);
  const userData = {
    Username: email,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);
  cognitoUser.authenticateUser(authDetails, {
    onSuccess: (result) => {
      recordDiagnostic("auth.completed", { operation: "auth.login" });
      authCallback(null, result);
    },
    onFailure: (err) => {
      reportError("auth.login", err);
      authCallback(err.message, null);
    },
    mfaRequired: function () {
      recordDiagnostic("auth.challenge", { reason: "mfa_required" });
      mfaCallback?.(cognitoUser, this);
    },
    newPasswordRequired: function (userAttributes) {
      recordDiagnostic("auth.challenge", { reason: "new_password_required" });
      delete userAttributes.email; // eslint-disable-line
      delete userAttributes.phone_number; // eslint-disable-line
      delete userAttributes.email_verified; // eslint-disable-line
      delete userAttributes.phone_number_verified; // eslint-disable-line
      newPasswordCallback(cognitoUser, userAttributes, this);
    },
  });
};

const clearVpnConnection = async () => {
  const serialNumber = sessionStorage.getItem("serialNumber");

  if (serialNumber !== null) {
    sessionStorage.removeItem("serialNumber");
    await disconnectDeviceVpn(serialNumber, { timeout: 10000 });
  }
};

let signingOut = false;
export const isSigningOut = () => signingOut;

export const signOut = async () => {
  if (signingOut) return;
  signingOut = true;
  try {
    await clearVpnConnection();
  } catch (error) {
    reportError("auth.logout.vpn_cleanup", error);
  } finally {
    try {
      userPool.getCurrentUser()?.signOut();
      localStorage.setItem("id", "");
      localStorage.setItem("access", "");
    } finally {
      recordDiagnostic("auth.completed", { operation: "auth.logout" });
      window.location.href = "/login";
      signingOut = false;
    }
  }
};

export const getCurrentUser = () =>
  new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error("Cannot fetch valid Cognitor User"));
    } else {
      cognitoUser.getSession((sessionErr) => {
        if (sessionErr) {
          reportError("auth.session", sessionErr);
          reject(sessionErr);
        } else {
          cognitoUser.getUserData((err, result) => {
            if (err) {
              reportError("auth.user_data", err);
              reject(err);
            } else {
              const { UserAttributes, UserMFASettingList } = result;
              UserAttributes.push({
                Name: "isMFAEnabled",
                Value: !!UserMFASettingList?.length,
              });
              resolve(UserAttributes);
            }
          });
        }
      });
    }
  });

export const getCurrentIdToken = () =>
  new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err) {
        reportError("auth.token", err);
        reject(err);
        return;
      }
      resolve(session?.idToken?.jwtToken || null);
    });
  });

export const isSessionValid = () => {
  let isCurrentSessionValid = false;
  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) return false;

  cognitoUser.getSession(async (err, session) => {
    if (err) {
      reportError("auth.session", err);
      return;
    }

    const payload = session?.idToken?.payload;

    // if token is expired
    if (!payload || Date.now() >= payload.exp * 1000) {
      return;
    }
    isCurrentSessionValid = session.isValid();
  });
  return isCurrentSessionValid;
};

export const forgotPassword = (email, callback) => {
  const userData = {
    Username: email,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);
  cognitoUser.forgotPassword({
    onSuccess: (result) => {
      recordDiagnostic("auth.completed", { operation: "auth.password_reset_request" });
      callback(null, result);
    },
    onFailure: (err) => {
      reportError("auth.password_reset_request", err);
      callback(err, null);
    },
  });
};

export const forgotPasswordSubmit = (email, code, password, callback) => {
  const userData = {
    Username: email,
    Pool: userPool,
  };
  const cognitoUser = new CognitoUser(userData);
  cognitoUser.confirmPassword(code, password, {
    onSuccess: (result) => {
      recordDiagnostic("auth.completed", { operation: "auth.password_reset" });
      callback(null, result);
    },
    onFailure: (err) => {
      reportError("auth.password_reset", err);
      callback(err, null);
    },
  });
};
